import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { COMPANY } from "../data";

const WA_GREETING = "Hoi! Ik ben Alexander. Als ik jou ergens mee kan helpen, laat me het dan gerust weten.";
const WA_LINK = `https://wa.me/${COMPANY.whatsapp}`;

const WhatsAppGlyph = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
    <path d="M16.003 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.73 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.6h.01c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.8-12.8-12.8Zm0 23.04h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.9 1.02 1.04-3.8-.25-.4a10.6 10.6 0 0 1-1.62-5.65c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.5 1.11 7.51 3.12a10.56 10.56 0 0 1 3.11 7.52c0 5.86-4.77 10.63-10.63 10.63Zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.58-.95-.85-1.59-1.9-1.78-2.22-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.72-.98-2.35-.26-.62-.52-.54-.71-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.11 1.09-1.11 2.65 0 1.56 1.14 3.07 1.3 3.28.16.21 2.25 3.44 5.45 4.82.76.33 1.36.53 1.82.68.77.24 1.46.21 2.01.13.61-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37Z" />
  </svg>
);

export const WhatsAppButton = () => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 3500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[90] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && !dismissed && (
          <motion.div
            key="wa-bubble"
            initial={{ opacity: 0, y: 16, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.9 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="whatsapp-bubble"
            className="relative w-[270px] md:w-[300px] bg-white rounded-2xl rounded-br-sm shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-black/5 overflow-hidden"
          >
            <button
              onClick={() => setDismissed(true)}
              aria-label="Sluiten"
              data-testid="whatsapp-bubble-close"
              className="absolute top-2 right-2 p-1 text-black/30 hover:text-black/70 transition-colors"
            >
              <X size={16} />
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-bubble-link"
              className="block px-4 pt-4 pb-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] text-white shrink-0">
                  <WhatsAppGlyph size={22} />
                </span>
                <div className="leading-tight">
                  <p className="font-display text-sm font-semibold text-[var(--ink)]">Alexander</p>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#25D366]">Online</p>
                </div>
              </div>
              <p className="text-sm text-black/70 leading-relaxed">{WA_GREETING}</p>
              <span className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-[#075E54] hover:gap-3 transition-[gap]">
                Start een WhatsApp-gesprek
                <span className="text-[#25D366]">→</span>
              </span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        aria-label="Chat via WhatsApp met Alexander"
        onMouseEnter={() => !dismissed && setOpen(true)}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white shadow-[0_8px_28px_rgba(37,211,102,0.45)]"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20" />
        <WhatsAppGlyph size={32} />
      </motion.a>
    </div>
  );
};
